/** Timezone helpers (locations run on local time, e.g. America/Denver). */

export const DEFAULT_TZ = "America/Denver";

/** One formatter per zone: building an Intl.DateTimeFormat costs far more than using one, and the demo seed and every
 *  Today calculation ask for the time thousands of times (measured: fix/staging-review-1, first-content time). */
const FORMATTERS = new Map<string, Intl.DateTimeFormat>();
function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = FORMATTERS.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    FORMATTERS.set(tz, f);
  }
  return f;
}

function parts(t: number, tz: string) {
  const f = formatterFor(tz);
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(new Date(t))) p[x.type] = x.value;
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day), h: Number(p.hour) % 24, mi: Number(p.minute), s: Number(p.second) };
}

/** Offset of `tz` from UTC at instant t, in ms (local − UTC). */
function offsetMs(t: number, tz: string): number {
  const p = parts(t, tz);
  return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - Math.floor(t / 1000) * 1000;
}

/** "YYYY-MM-DD" + "HH:MM" local in tz → ISO instant. */
export function zonedIso(date: string, hhmm: string, tz = DEFAULT_TZ): string {
  const [y, m, d] = date.split("-").map(Number);
  const [h, mi] = hhmm.split(":").map(Number);
  const guess = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  let t = guess - offsetMs(guess, tz);
  const second = guess - offsetMs(t, tz);
  if (second !== t) t = second;
  return new Date(t).toISOString();
}

/** Today's calendar date in tz. */
export function localDate(now: Date = new Date(), tz = DEFAULT_TZ): string {
  const p = parts(now.getTime(), tz);
  return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}

/** Local "HH:MM" in tz. */
export function localTime(now: Date = new Date(), tz = DEFAULT_TZ): string {
  const p = parts(now.getTime(), tz);
  return `${String(p.h).padStart(2, "0")}:${String(p.mi).padStart(2, "0")}`;
}

export function addDays(date: string, n: number): string {
  const t = Date.parse(date + "T00:00:00Z") + n * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}

/** Default print period for a template period, starting from `date`. */
export function periodFor(period: "day" | "week" | "month" | "event", date: string): { start: string; end: string } {
  if (period === "week") {
    const dow = new Date(date + "T00:00:00Z").getUTCDay(); // 0 = Sun
    const start = addDays(date, -((dow + 6) % 7)); // Monday
    return { start, end: addDays(start, 6) };
  }
  if (period === "month") {
    const start = date.slice(0, 8) + "01";
    const [y, m] = date.split("-").map(Number);
    const last = new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).getUTCDate();
    return { start, end: date.slice(0, 8) + String(last).padStart(2, "0") };
  }
  return { start: date, end: date };
}

/** "07:00" → "7:00 AM". */
export function prettyTime(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const hh = h ?? 0;
  return `${((hh + 11) % 12) + 1}:${String(m ?? 0).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
}

/** ISO instant → local "7:05 AM" in tz. */
export function isoToLocalTime(iso: string, tz = DEFAULT_TZ): string {
  return prettyTime(localTime(new Date(iso), tz));
}

export function relativeTime(iso: string, now = Date.now()): string {
  const mins = Math.round((now - Date.parse(iso)) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  return `${d} day${d > 1 ? "s" : ""} ago`;
}
